import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyRateTableConfig({ user }) {
  const { rateTableId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rateTable, setRateTable] = useState(null);
  
  // Digital Membership Config
  const [dmEnabled, setDmEnabled] = useState(false);
  const [dmAnnualPrice, setDmAnnualPrice] = useState(126);
  const [dmMonthlyPrice, setDmMonthlyPrice] = useState(10.5);
  const [dmProRateThreshold, setDmProRateThreshold] = useState(6);
  
  // Pro-rate Cutoff Config (for mid-term enrollment)
  const [proRateCutoffWeeks, setProRateCutoffWeeks] = useState(6);
  
  // Registration Fee Config
  const [regFeeEnabled, setRegFeeEnabled] = useState(false);
  const [regFeeAmount, setRegFeeAmount] = useState(50);
  const [regFeeDescription, setRegFeeDescription] = useState("One-time registration fee (includes 1 Academy Kit)");
  const [regFeeFrequency, setRegFeeFrequency] = useState("one_time");
  // What the registration fee grants as free kit (per rate table)
  const [regFeeKit, setRegFeeKit] = useState({ full_set: 1, jersey: 0, shorts: 0, socks: 0 });
  
  // Optional Items
  const [optionalItems, setOptionalItems] = useState([]);
  
  // Volume Pricing Config
  const [pricingMode, setPricingMode] = useState("progressive");
  const [volumePricingTiers, setVolumePricingTiers] = useState([
    { min_sessions: 1, price_per_session: 47 },
    { min_sessions: 2, price_per_session: 32 },
    { min_sessions: 3, price_per_session: 29 }
  ]);

  // Max Capacity (Fixed Term) Config — Jun 2026
  // Parent pays for max_sessions_per_week × weeks × price regardless of
  // how many sessions they pick. Prevents "book 1 attend 3" freeloading.
  const [maxCapacityPricePerSession, setMaxCapacityPricePerSession] = useState(30);
  const [maxSessionsPerWeek, setMaxSessionsPerWeek] = useState(3);

  // Universal session-selection cap (Jul 2026) — applies to ALL pricing modes.
  // Limits how many *distinct days per week* a parent may pick during registration.
  // 0 or empty = no cap.
  const [maxSessionsPerWeekCap, setMaxSessionsPerWeekCap] = useState(0);
  
  // Ad-hoc Pricing Config (Premium-based)
  const [termPassPremium, setTermPassPremium] = useState(6.4);
  const [nonMemberPremium, setNonMemberPremium] = useState(17);
  const [memberPremium, setMemberPremium] = useState(18.3);
  const [adhocLoading, setAdhocLoading] = useState(false);

  // "Target price" reverse calculators — admin types the price they want
  // to charge and we back-solve the % automatically.
  const [targetTermPassPrice, setTargetTermPassPrice] = useState("");
  const [targetMemberPrice, setTargetMemberPrice] = useState("");
  const [targetNonMemberPrice, setTargetNonMemberPrice] = useState("");

  // Example prices derived DYNAMICALLY from the current rate table config.
  // Base = the first volume-tier price (or Max-Capacity price if that mode is active).
  // Member rate = base × (1 − club_member_discount%).  Fallbacks kick in only when
  // the rate table has not yet been configured.
  const exampleBasePrice = (() => {
    if (pricingMode === "max_capacity" && maxCapacityPricePerSession) {
      return parseFloat(maxCapacityPricePerSession) || 47;
    }
    const tier1 = volumePricingTiers?.[0]?.price_per_session;
    return tier1 != null && tier1 !== "" ? parseFloat(tier1) : 47;
  })();
  const memberDiscountPct = (() => {
    const d = rateTable?.membership_discounts;
    if (!d) return 10;
    // Prefer club_member, fall back to section_member, else 10%.
    const v = d.club_member?.value ?? d.section_member?.value ?? 10;
    return parseFloat(v) || 0;
  })();
  const exampleMemberRate = Math.round(exampleBasePrice * (1 - memberDiscountPct / 100) * 100) / 100;

  // Reverse-calc helpers: target price → premium %
  const applyTargetPrice = (target, basePrice, setter) => {
    const t = parseFloat(target);
    if (!t || !basePrice) return;
    const pct = ((t / basePrice) - 1) * 100;
    setter(Math.round(pct * 100) / 100);  // round to 2 dp
  };
  
  const isAdmin = ["convenor", "captain"].includes(user?.role);

  useEffect(() => {
    if (isAdmin && rateTableId) {
      fetchRateTable();
      fetchAdhocPricing();
    }
  }, [isAdmin, rateTableId]);

  const fetchAdhocPricing = async () => {
    try {
      const response = await axios.get(`${API}/academy/adhoc-pricing`, { withCredentials: true });
      setTermPassPremium(response.data.term_pass_holder_premium_percent || 6.4);
      setNonMemberPremium(response.data.non_member_premium_percent || 17);
      setMemberPremium(response.data.member_premium_percent || 18.3);
    } catch (error) {
      console.error("Error fetching ad-hoc pricing:", error);
    }
  };

  const handleSaveAdhocPricing = async () => {
    try {
      setAdhocLoading(true);
      await axios.put(
        `${API}/academy/adhoc-pricing`,
        {
          term_pass_holder_premium_percent: parseFloat(termPassPremium),
          non_member_premium_percent: parseFloat(nonMemberPremium),
          member_premium_percent: parseFloat(memberPremium)
        },
        { withCredentials: true }
      );
      toast.success("Ad-hoc pricing saved successfully!");
    } catch (error) {
      console.error("Error saving ad-hoc pricing:", error);
      toast.error("Failed to save ad-hoc pricing");
    } finally {
      setAdhocLoading(false);
    }
  };

  const fetchRateTable = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/rate-tables`, { withCredentials: true });
      const table = response.data.find(t => t.id === rateTableId);
      
      if (!table) {
        toast.error("Rate table not found");
        navigate("/academy/rate-tables");
        return;
      }
      
      setRateTable(table);
      
      // Load Digital Membership Config
      const dmConfig = table.digital_membership_config || {};
      setDmEnabled(dmConfig.enabled ?? false);
      setDmAnnualPrice(dmConfig.annual_price ?? 120);
      setDmMonthlyPrice(dmConfig.monthly_price ?? 10);
      setDmProRateThreshold(dmConfig.pro_rate_threshold_months ?? 6);
      
      // Load Registration Fee Config
      const regFeeConfig = table.registration_fee || {};
      setRegFeeEnabled(regFeeConfig.enabled ?? false);
      setRegFeeAmount(regFeeConfig.amount ?? 50);
      setRegFeeDescription(regFeeConfig.description || "One-time registration fee (includes 1 Academy Kit)");
      setRegFeeFrequency(regFeeConfig.frequency || "one_time");
      const kitCounts = { full_set: 0, jersey: 0, shorts: 0, socks: 0 };
      const inclKit = regFeeConfig.included_kit_items;
      if (Array.isArray(inclKit) && inclKit.length) {
        inclKit.forEach((i) => {
          if (i && kitCounts.hasOwnProperty(i.item_type)) kitCounts[i.item_type] = Number(i.qty) || 0;
        });
      } else {
        kitCounts.full_set = 1; // historical default = 1 full set
      }
      setRegFeeKit(kitCounts);
      
      // Load Optional Items
      setOptionalItems(table.optional_items || []);
      
      // Load Volume Pricing Config
      setPricingMode(table.pricing_mode || "progressive");
      if (table.volume_pricing_tiers?.length > 0) {
        setVolumePricingTiers(table.volume_pricing_tiers);
      }

      // Load Max Capacity (Fixed Term) Config
      if (table.max_capacity_price_per_session != null) {
        setMaxCapacityPricePerSession(table.max_capacity_price_per_session);
      }
      if (table.max_sessions_per_week != null) {
        setMaxSessionsPerWeek(table.max_sessions_per_week);
      }

      // Load universal session-selection cap
      if (table.max_sessions_per_week_cap != null) {
        setMaxSessionsPerWeekCap(table.max_sessions_per_week_cap);
      }
      
      // Load Pro-rate Cutoff Config
      setProRateCutoffWeeks(table.pro_rate_cutoff_weeks ?? 6);
      
    } catch (error) {
      console.error("Error fetching rate table:", error);
      toast.error("Failed to load rate table");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const payload = {
        digital_membership_config: {
          enabled: dmEnabled,
          annual_price: parseFloat(dmAnnualPrice),
          monthly_price: parseFloat(dmMonthlyPrice),
          pro_rate_threshold_months: parseInt(dmProRateThreshold),
          expiry_method: "calendar_year"
        },
        registration_fee: {
          enabled: regFeeEnabled,
          amount: parseFloat(regFeeAmount),
          description: regFeeDescription,
          frequency: regFeeFrequency,
          includes_kit: Object.values(regFeeKit).some((q) => Number(q) > 0),
          included_kit_items: Object.entries(regFeeKit)
            .filter(([, q]) => Number(q) > 0)
            .map(([item_type, q]) => ({ item_type, qty: Number(q) })),
        },
        optional_items: optionalItems,
        pricing_mode: pricingMode,
        volume_pricing_tiers: pricingMode === "volume" ? volumePricingTiers.map(t => ({
          min_sessions: parseInt(t.min_sessions),
          price_per_session: parseFloat(t.price_per_session)
        })) : [],
        max_capacity_price_per_session: pricingMode === "max_capacity" ? parseFloat(maxCapacityPricePerSession) : null,
        max_sessions_per_week: pricingMode === "max_capacity" ? parseInt(maxSessionsPerWeek) : null,
        max_sessions_per_week_cap: parseInt(maxSessionsPerWeekCap) || 0,
        pro_rate_cutoff_weeks: parseInt(proRateCutoffWeeks)
      };
      
      await axios.put(
        `${API}/academy/rate-tables/${rateTableId}/config`,
        payload,
        { withCredentials: true }
      );
      
      toast.success("Configuration saved successfully!");
      navigate("/academy/rate-tables");
      
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const addOptionalItem = () => {
    setOptionalItems([
      ...optionalItems,
      {
        id: `item_${Date.now()}`,
        name: "",
        description: "",
        base_price: 0,
        enabled: true,
        category: "equipment",
        member_discounts: {
          section_member: 15,
          scc_digital: 10,
          scc_only: 10,
          digital_only: 5,
          non_member: 0
        }
      }
    ]);
  };

  const updateOptionalItem = (index, field, value) => {
    const updated = [...optionalItems];
    updated[index] = { ...updated[index], [field]: value };
    setOptionalItems(updated);
  };

  const removeOptionalItem = (index) => {
    setOptionalItems(optionalItems.filter((_, i) => i !== index));
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-6">
        <Card className="max-w-2xl mx-auto p-8 text-center">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h2>
          <p className="text-slate-400">Only administrators can manage rate table configurations.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-6">
        <div className="max-w-4xl mx-auto text-center py-12">
          <p className="text-slate-400">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/academy/rate-tables")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Rate Tables
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Rate Table Configuration</h1>
              <p className="text-sm text-slate-400">{rateTable?.name}</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        {/* Digital Membership Section */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">Digital Membership</h2>
              <p className="text-sm text-slate-400">Configure digital membership pricing and pro-rating</p>
            </div>
            <Switch checked={dmEnabled} onCheckedChange={setDmEnabled} />
          </div>
          
          {dmEnabled && (
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium mb-2">Annual Price ($)</label>
                <Input
                  type="number"
                  value={dmAnnualPrice}
                  onChange={(e) => setDmAnnualPrice(e.target.value)}
                  placeholder="120"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Monthly Price ($)</label>
                <Input
                  type="number"
                  value={dmMonthlyPrice}
                  onChange={(e) => setDmMonthlyPrice(e.target.value)}
                  placeholder="10"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Pro-rate Threshold (months)</label>
                <Input
                  type="number"
                  value={dmProRateThreshold}
                  onChange={(e) => setDmProRateThreshold(e.target.value)}
                  placeholder="6"
                />
                <p className="text-xs text-slate-400 mt-1">
                  If less than this many months remaining in the year, charge monthly rate instead of annual
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Registration Fee Section */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">Registration Fee</h2>
              <p className="text-sm text-slate-400">One-time or annual fee for new players</p>
            </div>
            <Switch checked={regFeeEnabled} onCheckedChange={setRegFeeEnabled} />
          </div>
          
          {regFeeEnabled && (
            <div className="space-y-4 mt-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium mb-2">Amount ($)</label>
                <Input
                  type="number"
                  value={regFeeAmount}
                  onChange={(e) => setRegFeeAmount(e.target.value)}
                  placeholder="50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea
                  value={regFeeDescription}
                  onChange={(e) => setRegFeeDescription(e.target.value)}
                  placeholder="What's included in this fee?"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Frequency</label>
                <select
                  value={regFeeFrequency}
                  onChange={(e) => setRegFeeFrequency(e.target.value)}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="one_time">One-time (lifetime)</option>
                  <option value="annual">Annual (per year)</option>
                </select>
              </div>
              <div className="pt-4 border-t">
                <label className="block text-sm font-medium mb-1">Free kit included in this fee</label>
                <p className="text-xs text-slate-400 mb-3">What each paid registration grants the player at no extra charge. Anything beyond this is a paid add-on at checkout.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {["full_set", "jersey", "shorts", "socks"].map((k) => (
                    <div key={k}>
                      <label className="block text-xs capitalize mb-1 text-slate-300">{k.replace("_", " ")}</label>
                      <Input
                        type="number"
                        min={0}
                        value={regFeeKit[k]}
                        onChange={(e) => setRegFeeKit((p) => ({ ...p, [k]: Math.max(0, parseInt(e.target.value) || 0) }))}
                        data-testid={`regfee-kit-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Volume Pricing Section */}
        <Card className="p-6 mb-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold">Pricing Mode</h2>
            <p className="text-sm text-slate-400">Choose how session prices are calculated</p>
          </div>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="pricingMode"
                  value="progressive"
                  checked={pricingMode === "progressive"}
                  onChange={(e) => setPricingMode(e.target.value)}
                  className="w-4 h-4"
                />
                <span className="font-medium">Progressive Discount</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="pricingMode"
                  value="volume"
                  checked={pricingMode === "volume"}
                  onChange={(e) => setPricingMode(e.target.value)}
                  className="w-4 h-4"
                />
                <span className="font-medium">Volume Pricing (All at Tier Rate)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="pricing-mode-max-capacity">
                <input
                  type="radio"
                  name="pricingMode"
                  value="max_capacity"
                  checked={pricingMode === "max_capacity"}
                  onChange={(e) => setPricingMode(e.target.value)}
                  className="w-4 h-4"
                />
                <span className="font-medium">Fixed Term (Max Capacity)</span>
              </label>
            </div>
            
            {pricingMode === "progressive" && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-300">
                  <strong>Progressive:</strong> 1st session at full price, 2nd session discounted, 3rd session more discounted, etc.
                </p>
              </div>
            )}
            
            {pricingMode === "volume" && (
              <>
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-sm text-emerald-300">
                    <strong>Volume Pricing:</strong> All sessions priced at the same tier rate based on total weekly sessions selected.
                    <br />Example: If parent selects 3 sessions/week, all 3 are charged at the 3-session rate.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Pricing Tiers</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setVolumePricingTiers([
                        ...volumePricingTiers,
                        { min_sessions: volumePricingTiers.length + 1, price_per_session: 0 }
                      ])}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Tier
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {volumePricingTiers.map((tier, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                        <div className="flex-1">
                          <label className="block text-xs text-slate-400 mb-1">Min Sessions/Week</label>
                          <Input
                            type="number"
                            min="1"
                            value={tier.min_sessions}
                            onChange={(e) => {
                              const updated = [...volumePricingTiers];
                              updated[index] = { ...tier, min_sessions: parseInt(e.target.value) };
                              setVolumePricingTiers(updated);
                            }}
                            className="h-9"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-slate-400 mb-1">Price/Session ($)</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.price_per_session}
                            onChange={(e) => {
                              const updated = [...volumePricingTiers];
                              updated[index] = { ...tier, price_per_session: parseFloat(e.target.value) };
                              setVolumePricingTiers(updated);
                            }}
                            className="h-9"
                          />
                        </div>
                        <div className="pt-5">
                          {volumePricingTiers.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setVolumePricingTiers(volumePricingTiers.filter((_, i) => i !== index))}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Preview */}
                  <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-white/10">
                    <h4 className="text-sm font-medium mb-2">Example (10 weeks):</h4>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      {volumePricingTiers.sort((a, b) => a.min_sessions - b.min_sessions).map((tier, idx) => (
                        <div key={idx} className="p-2 bg-slate-800 rounded text-center">
                          <p className="font-bold text-yellow-400">{tier.min_sessions} session{tier.min_sessions > 1 ? 's' : ''}/wk</p>
                          <p className="text-white">${tier.price_per_session}/session</p>
                          <p className="text-xs text-slate-400">
                            Total: ${(tier.price_per_session * tier.min_sessions * 10).toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {pricingMode === "max_capacity" && (
              <>
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg" data-testid="pricing-mode-max-capacity-help">
                  <p className="text-sm text-amber-200">
                    <strong>Fixed Term (Max Capacity):</strong> Parents always pay for the maximum sessions/week regardless of how many they actually pick.
                    <br />
                    Encourages full attendance and prevents parents from booking 1 session but attending all 3.
                    <br />
                    <em>Formula: price/session × max sessions/week × term weeks. Member and sibling discounts still apply; pro-rata for late registrations still applies.</em>
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Price per Session ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={maxCapacityPricePerSession}
                      onChange={(e) => setMaxCapacityPricePerSession(e.target.value)}
                      data-testid="max-capacity-price-per-session"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Max Sessions per Week</label>
                    <Input
                      type="number"
                      min="1"
                      max="7"
                      value={maxSessionsPerWeek}
                      onChange={(e) => setMaxSessionsPerWeek(e.target.value)}
                      data-testid="max-capacity-max-sessions-per-week"
                    />
                  </div>
                </div>

                <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-white/10">
                  <h4 className="text-sm font-medium mb-2">Preview (10 weeks):</h4>
                  <div className="text-sm space-y-1">
                    <p className="text-white">
                      <span className="text-slate-400">Term fee (non-member):</span>{" "}
                      <span className="font-bold text-amber-300" data-testid="max-capacity-preview-total">
                        ${(parseFloat(maxCapacityPricePerSession || 0) * parseInt(maxSessionsPerWeek || 0) * 10).toFixed(2)}
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">
                      = ${maxCapacityPricePerSession || 0}/session × {maxSessionsPerWeek || 0} sessions/week × 10 weeks
                    </p>
                    <p className="text-xs text-slate-400 mt-2">
                      Same price whether the parent picks 1, 2, or {maxSessionsPerWeek || 0} sessions per week.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Optional Items Section */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">Optional Items</h2>
              <p className="text-sm text-slate-400">Merchandise and equipment available during registration</p>
            </div>
            <Button onClick={addOptionalItem} variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
          
          <div className="space-y-4 mt-4">
            {optionalItems.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No optional items yet. Click &quot;Add Item&quot; to create one.</p>
            ) : (
              optionalItems.map((item, index) => (
                <div key={item.id} className="p-4 border rounded-lg bg-[#0a0f1a]">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={item.enabled}
                        onCheckedChange={(checked) => updateOptionalItem(index, "enabled", checked)}
                      />
                      <span className="text-sm font-medium">
                        {item.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOptionalItem(index)}
                      className="text-red-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Item Name</label>
                      <Input
                        value={item.name}
                        onChange={(e) => updateOptionalItem(index, "name", e.target.value)}
                        placeholder="Complete Kit Bag"
                        size="sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Base Price ($)</label>
                      <Input
                        type="number"
                        value={item.base_price}
                        onChange={(e) => updateOptionalItem(index, "base_price", parseFloat(e.target.value))}
                        placeholder="150"
                        size="sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium mb-1">Description</label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateOptionalItem(index, "description", e.target.value)}
                        placeholder="Boots + Shin Pads + Socks"
                        size="sm"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-medium mb-2">Member Discounts (%)</p>
                    <div className="grid grid-cols-5 gap-2 text-xs">
                      <div>
                        <label className="block mb-1">Section</label>
                        <Input
                          type="number"
                          value={item.member_discounts?.section_member || 15}
                          onChange={(e) => {
                            const discounts = { ...item.member_discounts, section_member: parseFloat(e.target.value) };
                            updateOptionalItem(index, "member_discounts", discounts);
                          }}
                          className="text-xs h-8"
                        />
                      </div>
                      <div>
                        <label className="block mb-1">SCC+DM</label>
                        <Input
                          type="number"
                          value={item.member_discounts?.scc_digital || 10}
                          onChange={(e) => {
                            const discounts = { ...item.member_discounts, scc_digital: parseFloat(e.target.value) };
                            updateOptionalItem(index, "member_discounts", discounts);
                          }}
                          className="text-xs h-8"
                        />
                      </div>
                      <div>
                        <label className="block mb-1">SCC Only</label>
                        <Input
                          type="number"
                          value={item.member_discounts?.scc_only || 10}
                          onChange={(e) => {
                            const discounts = { ...item.member_discounts, scc_only: parseFloat(e.target.value) };
                            updateOptionalItem(index, "member_discounts", discounts);
                          }}
                          className="text-xs h-8"
                        />
                      </div>
                      <div>
                        <label className="block mb-1">DM Only</label>
                        <Input
                          type="number"
                          value={item.member_discounts?.digital_only || 5}
                          onChange={(e) => {
                            const discounts = { ...item.member_discounts, digital_only: parseFloat(e.target.value) };
                            updateOptionalItem(index, "member_discounts", discounts);
                          }}
                          className="text-xs h-8"
                        />
                      </div>
                      <div>
                        <label className="block mb-1">None</label>
                        <Input
                          type="number"
                          value={item.member_discounts?.non_member || 0}
                          disabled
                          className="text-xs h-8 bg-slate-800/50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Session Selection Cap — universal across pricing modes */}
        <Card className="p-6 border-2 border-orange-500/30" data-testid="session-cap-card">
          <div className="mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded">CAP</span>
              Session Selection Cap
            </h2>
            <p className="text-sm text-slate-400">
              Universal limit on how many <strong>distinct days per week</strong> a parent may pick during registration. Applies to all pricing modes.
            </p>
          </div>

          <div className="space-y-4">
            <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-sm">
              <p className="text-orange-300 font-medium mb-2">How it works:</p>
              <ul className="text-orange-200 space-y-1 text-xs">
                <li>• Parent picks e.g. Monday &amp; Tuesday → 2 days/week × term weeks</li>
                <li>• Once the cap is reached, additional session cards become disabled with a &quot;Max N sessions/week&quot; badge</li>
                <li>• Set to <strong>0</strong> (or leave empty) for <strong>no cap</strong></li>
                <li>• Backend also validates on submit so the cap can&apos;t be bypassed via the API</li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Max Days per Week
                </label>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    max="7"
                    value={maxSessionsPerWeekCap}
                    onChange={(e) => setMaxSessionsPerWeekCap(e.target.value)}
                    className="pr-16"
                    data-testid="max-sessions-per-week-cap-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">days/wk</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  0 = no cap. Common values: 2 (Mon+Tue), 3 (Mon+Wed+Fri).
                </p>
              </div>
              <div className="flex items-center">
                <div className="p-3 bg-slate-800/50 rounded-lg w-full">
                  <p className="text-sm text-slate-300">
                    {parseInt(maxSessionsPerWeekCap) > 0
                      ? <>Current: parent can pick <strong className="text-orange-300">up to {maxSessionsPerWeekCap} days/week</strong> from any offered sessions.</>
                      : <>No cap — parent can pick every available session in the term.</>}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Mid-Term Enrollment Cutoff */}
        <Card className="p-6 border-2 border-blue-500/30">
          <div className="mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">TERM</span>
              Pro-Rate Enrollment Cutoff
            </h2>
            <p className="text-sm text-slate-400">
              After this threshold, only single sessions or "commit to next term" options are available
            </p>
          </div>

          <div className="space-y-4">
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
              <p className="text-blue-300 font-medium mb-2">How it works:</p>
              <ul className="text-blue-200 space-y-1 text-xs">
                <li>• When fewer than <strong>{proRateCutoffWeeks} weeks</strong> remain in term, pro-rate enrollment is disabled</li>
                <li>• Parents can still: book single sessions OR commit to next term (gets term rate for remaining weeks)</li>
                <li>• This prevents confusing situations where someone pays for just 1-2 remaining weeks</li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Minimum Weeks Required for Pro-Rate
                </label>
                <div className="relative">
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={proRateCutoffWeeks}
                    onChange={(e) => setProRateCutoffWeeks(e.target.value)}
                    className="pr-16"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">weeks</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Default: 6 weeks
                </p>
              </div>
              <div className="flex items-center">
                <div className="p-3 bg-slate-800/50 rounded-lg w-full">
                  <p className="text-sm text-slate-300">
                    Example: In a 10-week term, pro-rate available until week {10 - proRateCutoffWeeks}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Weeks 1-{10 - proRateCutoffWeeks}: Pro-rate ✓ | Weeks {10 - proRateCutoffWeeks + 1}-10: Single only
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Ad-hoc Session Pricing */}
        <Card className="p-6 border-2 border-orange-500/30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded">GLOBAL</span>
                Ad-hoc Session Premiums
              </h2>
              <p className="text-sm text-slate-400">
                Premium % applied to all ad-hoc session bookings to encourage term commitment
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Who pays what */}
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
              <p className="text-blue-300 font-medium mb-2">Pricing Logic:</p>
              <ul className="text-blue-200 space-y-1 text-xs">
                <li>• <strong>Term Pass Holder:</strong> Session rate + Term Pass Premium % (rewards commitment but still charges for flexibility)</li>
                <li>• <strong>SCC Member (no term pass):</strong> Member rate + Member Premium %</li>
                <li>• <strong>Non-Member (no term pass):</strong> Base price + Non-Member Premium %</li>
              </ul>
            </div>

            <div className="p-4 bg-slate-900/50 rounded-lg border border-white/10">
              {/* Live base-rate readout */}
              <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-xs text-blue-300">
                  Premiums are calculated live from this rate table:
                  {" "}<span className="font-semibold text-white">Base ${exampleBasePrice.toFixed(2)}</span>
                  {" "}·{" "}<span className="font-semibold text-white">Member rate ${exampleMemberRate.toFixed(2)}</span>
                  {" "}(after {memberDiscountPct}% member discount)
                </p>
                <p className="text-[10px] text-slate-400 mt-1">
                  💡 To change the base, edit the {pricingMode === "max_capacity" ? "Max-Capacity Price/Session" : "1-session/wk price in Volume Pricing"} above. Save the rate table for the change to persist.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Term Pass Holder Premium
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">+</span>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={termPassPremium}
                      onChange={(e) => setTermPassPremium(e.target.value)}
                      className="pl-7 pr-7"
                      data-testid="term-pass-premium-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Applied to their term rate (${exampleMemberRate.toFixed(2)} member / ${exampleBasePrice.toFixed(2)} non-member)
                  </p>
                  {/* Reverse calculator */}
                  <div className="mt-2 flex items-center gap-1">
                    <span className="text-[10px] text-slate-500 whitespace-nowrap">or target $</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 45"
                      value={targetTermPassPrice}
                      onChange={(e) => setTargetTermPassPrice(e.target.value)}
                      className="h-7 text-xs"
                      data-testid="target-term-pass-price-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px] px-2"
                      onClick={() => applyTargetPrice(targetTermPassPrice, exampleMemberRate, setTermPassPremium)}
                      data-testid="target-term-pass-apply"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    SCC Member Premium
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">+</span>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={memberPremium}
                      onChange={(e) => setMemberPremium(e.target.value)}
                      className="pl-7 pr-7"
                      data-testid="member-premium-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Applied to member rate (${exampleMemberRate.toFixed(2)})</p>
                  <div className="mt-2 flex items-center gap-1">
                    <span className="text-[10px] text-slate-500 whitespace-nowrap">or target $</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 50"
                      value={targetMemberPrice}
                      onChange={(e) => setTargetMemberPrice(e.target.value)}
                      className="h-7 text-xs"
                      data-testid="target-member-price-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px] px-2"
                      onClick={() => applyTargetPrice(targetMemberPrice, exampleMemberRate, setMemberPremium)}
                      data-testid="target-member-apply"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Non-Member Premium
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">+</span>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={nonMemberPremium}
                      onChange={(e) => setNonMemberPremium(e.target.value)}
                      className="pl-7 pr-7"
                      data-testid="non-member-premium-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Applied to base price (${exampleBasePrice.toFixed(2)})</p>
                  <div className="mt-2 flex items-center gap-1">
                    <span className="text-[10px] text-slate-500 whitespace-nowrap">or target $</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 55"
                      value={targetNonMemberPrice}
                      onChange={(e) => setTargetNonMemberPrice(e.target.value)}
                      className="h-7 text-xs"
                      data-testid="target-non-member-price-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px] px-2"
                      onClick={() => applyTargetPrice(targetNonMemberPrice, exampleBasePrice, setNonMemberPremium)}
                      data-testid="target-non-member-apply"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>

              {/* Example Calculation */}
              <div className="mt-4 p-3 bg-slate-800 rounded-lg">
                <p className="text-xs text-slate-400 mb-3 font-medium">
                  Example Calculation (based on ${exampleBasePrice.toFixed(2)} session, {memberDiscountPct}% member discount):
                </p>
                
                {/* Term Enrollment - Best Value */}
                <div className="mb-3 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded text-center">
                  <p className="text-xs text-emerald-400 font-medium">🏆 TERM ENROLLMENT (Best Value)</p>
                  <div className="flex justify-center gap-8 mt-2">
                    <div>
                      <p className="text-xs text-slate-400">Member</p>
                      <p className="text-lg font-bold text-emerald-400">${exampleMemberRate.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Non-Member</p>
                      <p className="text-lg font-bold text-emerald-400">${exampleBasePrice.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Ad-hoc Pricing Grid */}
                <p className="text-xs text-slate-400 mb-2 text-center">Ad-hoc Session Prices (with premiums)</p>
                <div className="grid grid-cols-2 gap-3 text-center">
                  {/* Term Pass Holders */}
                  <div className="p-2 bg-green-500/10 border border-green-500/30 rounded">
                    <p className="text-xs text-green-400 font-medium mb-2">Term Pass Holder + Ad-hoc</p>
                    <div className="flex justify-around">
                      <div>
                        <p className="text-xs text-slate-400">Member</p>
                        <p className="text-md font-bold text-green-400">
                          ${(exampleMemberRate * (1 + termPassPremium / 100)).toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-400">${exampleMemberRate} + {termPassPremium}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Non-Member</p>
                        <p className="text-md font-bold text-green-400">
                          ${(exampleBasePrice * (1 + termPassPremium / 100)).toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-400">${exampleBasePrice} + {termPassPremium}%</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* No Term Pass */}
                  <div className="p-2 bg-orange-500/10 border border-orange-500/30 rounded">
                    <p className="text-xs text-orange-400 font-medium mb-2">No Term Pass (Ad-hoc only)</p>
                    <div className="flex justify-around">
                      <div>
                        <p className="text-xs text-slate-400">Member</p>
                        <p className="text-md font-bold text-blue-400">
                          ${(exampleMemberRate * (1 + memberPremium / 100)).toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-400">${exampleMemberRate} + {memberPremium}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Non-Member</p>
                        <p className="text-md font-bold text-orange-400">
                          ${(exampleBasePrice * (1 + nonMemberPremium / 100)).toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-400">${exampleBasePrice} + {nonMemberPremium}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Save button */}
            <Button 
              onClick={handleSaveAdhocPricing} 
              disabled={adhocLoading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {adhocLoading ? "Saving..." : "Save Ad-hoc Premiums"}
            </Button>

            {/* Note */}
            <div className="text-xs text-slate-400 p-3 bg-slate-900/30 rounded">
              <strong>Tip:</strong> Set premiums so that ad-hoc is always more expensive than term enrollment. 
              Even term pass holders should pay a small premium for the flexibility of ad-hoc bookings.
              Break-even point: If your target ad-hoc price is $50 and member rate is $42.30, set Term Pass Premium to ~18.2%.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
